import { TypographyH1, TypographyH4, TypographyMuted, TypographyXS } from "@/components/ui/typography"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover" 
import { useUserQuery } from "@/queries/useUserQuery"
import VirtualizedCommand from "@/components/VirtualizedCommand"
import { useEffect, useState } from "react"
import Button from "@/components/Button"
import { User } from "@/types/User"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { getInitials } from "@/utils/stringFormatter"
import { useResaleQuery } from "@/queries/useResaleQuery"
import { useEmployeeQuery } from "@/queries/useEmployeeQuery"
import { Input } from "@/components/ui/input" 
import { Checkbox } from "@/components/ui/checkbox"
import { Employee } from "@/types/Employee" 
import { useUserPermissionsQuery } from "@/queries/useUserPermissionsQuery"
import CardEmployee from "./components/CardEmployee"
import { ShieldUser } from "lucide-react"


const UserPermissions = () => {
    const [selectUser, setSelectUser] = useState<number>(0)
    const [selectResale, setSelectResale] = useState<number>(0)
    const [user, setUser] = useState<User>()
    const [enableFetchEmployee, setEnableFetchEmployee] = useState<boolean>(false)
    const [enableFetchUserPermissions, setEnableFetchUserPermissions] = useState<boolean>(false)
    const [filterName, setFilterName] = useState<string>('')
    const [filterRole, setFilterRole] = useState<string>('')
    const [filterPurgeInactive, setFilterPurgeInactive] = useState<boolean>(true)
    const [listRoles, setListRoles] = useState<string[]>([])
    const [listEmployee, setListEmbloyee] = useState<Employee[]>([])

    const { data: dataUser} = useUserQuery()
    const { data: dataUserPermissions , refetch: refetchUserPermissions } = useUserPermissionsQuery(selectUser, enableFetchUserPermissions)
    const { data: dataResale } = useResaleQuery()
    const { data: dataEmployee , refetch: refetchEmployee } = useEmployeeQuery(selectResale, enableFetchEmployee)
        
    useEffect(() => {   
        setEnableFetchEmployee(true)
        refetchEmployee()
    }, [selectResale]);

    useEffect(() => {   
       if(dataEmployee) {
           setListRoles([...new Set(dataEmployee.map((item)=> item.DD_CARGO))])
           setListEmbloyee(dataEmployee)
       }
    }, [dataEmployee]);
  
    useEffect(() => {   
        if(dataEmployee) {
            let filterData = dataEmployee

            if(filterName) {
                filterData = filterData.filter(item => item.NM_FUNCIONARIO.toLowerCase().includes(filterName.toLowerCase()))
            }
            if(filterRole) {
                filterData = filterData.filter(item => item.DD_CARGO == filterRole)
            }
            if(filterPurgeInactive) {
                filterData = filterData.filter(item => item.DD_TIPO_VENDEDOR != 'INATIVO')
            }
             setListEmbloyee(filterData)
        }
        
    }, [filterName, filterRole, filterPurgeInactive, dataEmployee]);

    useEffect(() => {
        if(selectUser && dataUser) {
            setUser(dataUser?.find(item => item.SK_USUARIO == selectUser))
            setEnableFetchUserPermissions(true)
            refetchUserPermissions()
        } 
    }, [selectUser]);
 
    return (
        <div className="w-full min-h-full p-4 overflow-hidden bg-[#5a8db50d]">
            <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                        <ShieldUser className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <TypographyH1>
                            Permissões de acesso a representantes
                        </TypographyH1>
                        <TypographyMuted>
                            Gerencie o acesso dos usuários aos cadastros de representantes de cada revenda
                        </TypographyMuted>
                    </div>
                </div>
            </div>
             
            <div className="border rounded-lg p-4 mt-6 bg-white">
                <TypographyH4 className="mb-4">
                    Seleção de usuário
                </TypographyH4>
                <div className="grid md:grid-cols-2 md:gap-14 gap-2">
                    {dataUser && (
                        <div className="md:max-w-[350px]">
                            <TypographyXS className="flex-1 text-start text-sm mb-2">
                                Selecione o usuário para gerenciar permissões:
                            </TypographyXS>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="justify-start w-full gap-2 text-xs"
                                    >
                                        {selectUser == 0 ? (
                                            <TypographyXS className="flex-1 text-start">
                                                Selecione um usuário
                                            </TypographyXS>
                                        ) : (
                                            <>
                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                    {dataUser.find(supp => supp.SK_USUARIO == selectUser)?.NM_USUARIO}
                                                </TypographyXS>
                                            </>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                    <VirtualizedCommand
                                        options={dataUser.map(item => { return { value: item.SK_USUARIO.toString(), label: item?.NM_USUARIO } })}
                                        placeholder="Usuário" 
                                        selectedValues={{ [`${selectUser}`]: true }}
                                        onSelectionChange={(data) => {
                                            setSelectUser(Number(data)) 
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    )}
                    {user && (
                        <div className="mt-4 bg-[#5a8db50d] p-4 rounded-lg border max-w-[500px]">   
                            <div className="flex items-center gap-2">
                                <Avatar className="w-10 h-10 text-xs">
                                    <AvatarFallback className="bg-primary-500 text-gray-50 text-lg">
                                        {getInitials(user.NM_USUARIO || '')}
                                    </AvatarFallback>  
                                </Avatar>
                                <div>
                                    <TypographyXS className="flex-1 text-start text-sm mb-2">
                                        {user.NM_USUARIO}
                                    </TypographyXS>
                                    <TypographyMuted>
                                        {user.DD_EMAIL}
                                    </TypographyMuted>

                                </div>
                            </div>
                        </div>
                    )} 
                </div>
                {!user && (
                    <div className="mt-4 bg-[#5a8db50d] p-4 rounded-3xl border max-w-[500px]">   
                        <TypographyXS className="flex-1 text-start text-sm mb-2">
                            Como usar esta página
                        </TypographyXS>
                        <ul className="list-disc px-4">
                            <li className="text-sm">Selecione um usuário na lista acima</li>
                            <li className="text-sm">Visualize os centros de custo disponíveis</li>
                            <li className="text-sm">Adicione ou remova permissões conforme necessário</li>
                            <li className="text-sm">As alterações são salvas automaticamente</li>
                        </ul>  
                    </div>
                )}

                {dataResale && user && (
                        <div className="md:max-w-[350px]">
                            <TypographyXS className="flex-1 text-start text-sm mb-2">
                                Selecione uma revenda para gerenciar o acesso a ela:
                            </TypographyXS>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="justify-start w-full gap-2 text-xs"
                                    >
                                        {selectResale == 0 ? (
                                            <TypographyXS className="flex-1 text-start">
                                                Selecione uma revenda
                                            </TypographyXS>
                                        ) : (
                                            <>
                                                <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                    {dataResale.find(supp => supp.SK_REVENDA == selectResale)?.DS_REVENDA}
                                                </TypographyXS>
                                            </>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="z-50 p-0 h-60 w-96" align="start">
                                    <VirtualizedCommand
                                        options={dataResale.map(item => { return { value: item.SK_REVENDA.toString(), label: `${item?.DS_REVENDA} (${item?.SK_REVENDA})` } })}
                                        placeholder="Revenda" 
                                        selectedValues={{ [`${selectResale}`]: true }}
                                        onSelectionChange={(data) => {
                                            setSelectResale(Number(data)) 
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    )}
            </div>
            {user && (
                <div className="border rounded-lg p-4 mt-2 bg-white">
                    
                    { dataEmployee && ( 
                        <div className="grid gap-3 lg:grid-cols-3 md:grid-cols-2 grid-cols-1">
                            <div>
                                <TypographyXS className="flex-1 text-start text-sm mb-2"> Nome funcionário</TypographyXS> 
                                <Input  
                                    onChange={(e) =>
                                        setFilterName(e.target.value)
                                    }
                                /> 
                            </div>
                            <div>
                                <TypographyXS className="flex-1 text-start text-sm mb-2"> Cargos </TypographyXS>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="justify-start w-full gap-2 text-xs"
                                        >
                                            {filterRole == '' ? (
                                                <TypographyXS className="flex-1 text-start">
                                                    Filtre por um cargo
                                                </TypographyXS>
                                            ) : (
                                                <>
                                                    <TypographyXS className="p-1 px-2 overflow-hidden text-xs font-normal text-accent-foreground text-ellipsis">
                                                        {listRoles.find(supp => supp == filterRole)}
                                                    </TypographyXS>
                                                </>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="z-50 p-0 w-96" align="start">
                                        <VirtualizedCommand
                                            options={listRoles.map(item => { return { value: item.toString(), label: item } })}
                                            placeholder="Cargo" 
                                            selectedValues={{ [`${filterRole}`]: true }}
                                            onClearFilters={() => setFilterRole('')}
                                            onSelectionChange={(data) => {
                                                setFilterRole(data) 
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="flex items-center">
                                 <div className="flex flex-row items-center space-x-3 space-y-0"> 
                                        <Checkbox 
                                            onCheckedChange={(checked) => setFilterPurgeInactive(checked ? true : false)}
                                            checked={filterPurgeInactive}
                                        /> 
                                    <TypographyXS className="space-y-1 leading-none text-sm"> 
                                        Expurgar inativos
                                    </TypographyXS>
                                </div>
                            </div>
                        </div>
                    )}
                    { listEmployee && (
                        <div className="max-h-screen grid gap-3 lg:grid-cols-3 md:grid-cols-2 grid-cols-1 overflow-auto mt-4">
                            {listEmployee.map((item) => (
                                <CardEmployee key={item.SK_FUNCIONARIO} dataUserPermissions={dataUserPermissions || []} listEmployee={item} idUser={selectUser}/>
                            ))}
                        </div>
                    )} 
                </div>
            )}

            
        </div>
    )
}

export default UserPermissions
